import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, clearToken, getToken, setToken, setActiveAppId, getActiveAppId } from "./api";

export type SessionUser = {
  id: string;
  orgId: string;
  email: string;
  mobile: string;
  name: string;
  role: string;
  extension: string;
  isActive: boolean;
  sipPasswordSet?: boolean;
  phoneMode?: "desk" | "sip";
};

export type SessionOrg = {
  id: string;
  name: string;
  slug: string;
  plan: string;
  planLabel: string;
  status: string;
  seats: number;
  seatsUsed: number;
  trialEndsAt: string;
  isPlatform: boolean;
  locked: boolean;
};

type NeronApp = {
  id: number | string;
  name?: string;
  slug?: string;
  status?: string;
  is_default?: boolean;
};

type NeronUser = {
  id: number | string;
  name?: string;
  mobile?: string;
  extension?: string | null;
  role?: string;
  status?: string;
  app_id?: number | string | null;
  app?: NeronApp | null;
};

type Session = {
  user: SessionUser | null;
  org: SessionOrg | null;
  orgName: string;
  loading: boolean;
  login: (mobile: string, otp: string) => Promise<void>;
  register: (orgName: string, name: string, mobile: string, otp: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  setApp: (app: NeronApp) => void;
};

const Ctx = createContext<Session | null>(null);

function mapRole(role?: string) {
  const r = String(role || "agent").toLowerCase();
  if (r === "admin") return "admin";
  if (r === "owner" || r === "super_admin") return r;
  if (r === "supervisor") return "supervisor";
  return "agent";
}

function mapOrg(app?: NeronApp | null): SessionOrg {
  return {
    id: String(app?.id || getActiveAppId() || "1"),
    name: String(app?.name || "Bmtax"),
    slug: String(app?.slug || "bmtax"),
    plan: "business",
    planLabel: app?.is_default ? "Default app" : "App",
    status: String(app?.status || "active"),
    seats: 50,
    seatsUsed: 1,
    trialEndsAt: "",
    isPlatform: Boolean(app?.is_default),
    locked: false,
  };
}

function mapUser(u: NeronUser & { phoneMode?: string; phone_mode?: string; sipPasswordSet?: boolean }) {
  const mode = String(u.phoneMode || u.phone_mode || "desk").toLowerCase();
  return {
    id: String(u.id),
    orgId: String(u.app_id || u.app?.id || getActiveAppId() || "1"),
    email: "",
    mobile: String(u.mobile || ""),
    name: String(u.name || u.mobile || "User"),
    role: mapRole(u.role),
    extension: String(u.extension || ""),
    isActive: String(u.status || "active") !== "disabled",
    sipPasswordSet: Boolean(u.sipPasswordSet),
    phoneMode: mode === "sip" ? ("sip" as const) : ("desk" as const),
  };
}

function applyUser(data: NeronUser) {
  const org = mapOrg(data.app || (data.app_id ? { id: data.app_id } : null));
  if (!getActiveAppId() && org.id) setActiveAppId(org.id);
  return { user: mapUser(data), org };
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    if (!getToken()) {
      setUser(null);
      setOrg(null);
      setLoading(false);
      return;
    }
    try {
      const data = await api<NeronUser>("/auth/me");
      const mapped = applyUser(data);
      setUser(mapped.user);
      setOrg(mapped.org);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      // Only drop the session on a real auth failure — not network/transient errors.
      if (status === 401) {
        clearToken();
        setUser(null);
        setOrg(null);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<Session>(
    () => ({
      user,
      org,
      orgName: org?.name || "",
      loading,
      async login(mobile, otp) {
        const res = await api<{ token: string; data: NeronUser }>("/auth/verify-otp", {
          method: "POST",
          body: JSON.stringify({ mobile, otp }),
        });
        const token = (res as { token?: string }).token;
        const data = (res as { data?: NeronUser }).data || (res as unknown as NeronUser);
        if (!token) throw new Error("No token returned from server");
        setToken(token);
        const mapped = applyUser(data);
        setUser(mapped.user);
        setOrg(mapped.org);
        await refresh();
      },
      async register(orgName, name, mobile, otp) {
        const res = await api<{ token: string; data: NeronUser }>("/auth/register", {
          method: "POST",
          body: JSON.stringify({ orgName, name, mobile, otp }),
        });
        const token = (res as { token?: string }).token;
        const data = (res as { data?: NeronUser }).data || (res as unknown as NeronUser);
        if (!token) throw new Error("No token returned from server");
        setToken(token);
        const mapped = applyUser(data);
        setUser(mapped.user);
        setOrg(mapped.org);
        if (mapped.org?.id) setActiveAppId(mapped.org.id);
        await refresh();
      },
      logout() {
        clearToken();
        setUser(null);
        setOrg(null);
        window.location.href = "/";
      },
      setApp(app) {
        setActiveAppId(app.id);
        setOrg(mapOrg(app));
      },
      refresh,
    }),
    [user, org, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession outside provider");
  return ctx;
}
